import type { GetServerSideProps } from 'next';
import React from 'react';
import Archive from 'components/archive';
import AuthorInfo from 'components/author-info';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';
import RoundedSection from 'components/rounded-section';
import { getAuthorPage, AuthorPageProps } from 'lib/queries/blog';
import { useWordPressContext } from 'contexts/wordpress-provider';

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59',
  );

  return await getAuthorPage(context);
};

export default function AuthorPage({
  page: {
    id,
    name,
    modified,
    description,
    uri,
    seo,
    acf: { blocks, image, list, quote, socialNetworks, displayName },
  },
  postsList,
  reviewPostsList,
  expertPostsList,
}: AuthorPageProps) {
  const { translations, authorsPageRoundedSection } = useWordPressContext();
  const numPosts = postsList?.pagination?.total;
  const numReviewPosts = reviewPostsList?.pagination?.total;
  const numExpertPosts = expertPostsList?.pagination?.total;

  return (
    <PageWrap seo={seo} title={name} pageType="author">
      <AuthorInfo
        id={id}
        displayName={displayName}
        name={name}
        description={description}
        uri={uri}
        image={image}
        jobTitles={list?.length > 0 ? list.map(({ jobTitle }) => jobTitle) : []}
        quote={quote}
        socialNetworks={socialNetworks}
        isPageTitle={true}
        variant="xlarge"
      />
      <Blocks blocks={blocks} pageType="author" modified={modified} />
      {postsList?.posts?.length > 0 ? (
        <Archive
          posts={postsList.posts}
          pagination={postsList.pagination}
          variant="smallFeatured"
          title={translations[`{articles} articles written by {name}`]
            .replace('{articles}', numPosts.toString())
            .replace('{name}', displayName || name)}
          className="leafwell-author-profile"
        />
      ) : null}
      {reviewPostsList?.posts?.length > 0 ? (
        <Archive
          posts={reviewPostsList.posts}
          pagination={reviewPostsList?.pagination}
          variant="smallFeatured"
          title={translations[`{articles} articles reviewed by {name}`]
            .replace('{articles}', numReviewPosts.toString())
            .replace('{name}', displayName || name)}
          queryParamName="review_page"
          className="leafwell-author-profile"
        />
      ) : null}
      {expertPostsList?.posts?.length > 0 ? (
        <Archive
          posts={expertPostsList.posts}
          pagination={expertPostsList?.pagination}
          variant="smallFeatured"
          title={translations[
            `{articles} articles featuring {name} as an expert`
          ]
            .replace('{articles}', numExpertPosts.toString())
            .replace('{name}', displayName || name)}
          queryParamName="expert_page"
          className="leafwell-author-profile"
        />
      ) : null}
      <RoundedSection {...authorsPageRoundedSection} />
    </PageWrap>
  );
}
